/**
 * useAnalogLive - Custom hook for live analog input visualization
 * Subscribes to serial data, parses analog streaming events
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getSerialConnection } from '@/lib/serialConnection';

export interface AnalogLiveState {
  sliders: [number, number, number]; // 0-127, -1 = no data yet
  pedal: { type: 'none' | 'sustain' | 'expression'; value: number };
  connected: boolean; // true if receiving data
}

const INITIAL_STATE: AnalogLiveState = {
  sliders: [-1, -1, -1],
  pedal: { type: 'none', value: 0 },
  connected: false,
};

// [A] S1=64 S2=100 S3=0 P=sus:on
// [A] S1=64 S2=100 S3=0 P=exp:78
// [A] S1=64 S2=100 S3=0 P=none
const ANALOG_RE =
  /\[A\]\s+S1=(\d+)\s+S2=(\d+)\s+S3=(\d+)\s+P=(none|sus:on|sus:off|exp:(\d+))/;

const STALE_TIMEOUT_MS = 3000;

export function useAnalogLive(enabled: boolean): AnalogLiveState {
  const [state, setState] = useState<AnalogLiveState>(INITIAL_STATE);
  const lineBufferRef = useRef('');
  const enabledRef = useRef(enabled);
  const lastDataRef = useRef(0);
  enabledRef.current = enabled;

  const processLine = useCallback((line: string) => {
    const m = ANALOG_RE.exec(line);
    if (!m) return;

    const s1 = parseInt(m[1]!, 10);
    const s2 = parseInt(m[2]!, 10);
    const s3 = parseInt(m[3]!, 10);
    const pedalStr = m[4]!;

    let pedal: AnalogLiveState['pedal'];
    if (pedalStr === 'none') {
      pedal = { type: 'none', value: 0 };
    } else if (pedalStr === 'sus:on') {
      pedal = { type: 'sustain', value: 127 };
    } else if (pedalStr === 'sus:off') {
      pedal = { type: 'sustain', value: 0 };
    } else {
      // exp:N
      pedal = { type: 'expression', value: parseInt(m[5]!, 10) };
    }

    lastDataRef.current = Date.now();
    setState({ sliders: [s1, s2, s3], pedal, connected: true });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState(INITIAL_STATE);
      return;
    }

    const conn = getSerialConnection();

    const unsubData = conn.onData((data: string) => {
      if (!enabledRef.current) return;
      lineBufferRef.current += data;

      const lines = lineBufferRef.current.split('\n');
      lineBufferRef.current = lines.pop() ?? '';
      for (const line of lines) {
        processLine(line.trim());
      }
    });

    const unsubState = conn.onStateChange((connState: string) => {
      if (connState === 'connected' && enabledRef.current) {
        setTimeout(() => {
          conn.send('analog on\r\n').catch(() => {});
        }, 500);
      }
      if (connState === 'disconnected') {
        setState(INITIAL_STATE);
      }
    });

    // If already connected, enable streaming
    if (conn.isConnected()) {
      conn.send('analog on\r\n').catch(() => {});
    }

    // Mark disconnected if no data for a while
    const staleInterval = setInterval(() => {
      if (Date.now() - lastDataRef.current > STALE_TIMEOUT_MS) {
        setState((prev) => (prev.connected ? { ...prev, connected: false } : prev));
      }
    }, 1000);

    return () => {
      unsubData();
      unsubState();
      clearInterval(staleInterval);
      lineBufferRef.current = '';
      if (conn.isConnected()) {
        conn.send('analog off\r\n').catch(() => {});
      }
    };
  }, [enabled, processLine]);

  return state;
}
